/**
 * Authentication Context
 * Provides authentication state and methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService, getStoredUser, getStoredToken, clearAuthData } from '../utils/authApi';

// Create context
const AuthContext = createContext(null);

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // First check if we have stored auth
        const token = getStoredToken();
        const storedUser = getStoredUser();
        
        if (!token) {
          setLoading(false);
          return;
        }

        // Verify token is still valid
        const status = await authService.checkStatus();
        
        if (status.authenticated && status.user) {
          setUser(status.user);
          setIsAuthenticated(true);
        } else {
          clearAuthData();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Login function
  const login = useCallback(async (email, password) => {
    const result = await authService.login(email, password);
    setUser(result.user);
    setIsAuthenticated(true);
    return result;
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  // Refresh user data
  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user:', error);
      return null;
    }
  }, []);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  // Context value
  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// HOC for protected routes
export const withAuth = (Component) => {
  return function ProtectedRoute(props) {
    const { isAuthenticated, loading } = useAuth();
    
    if (loading) {
      return null; // Or loading spinner
    }
    
    if (!isAuthenticated) {
      // Redirect handled in App.js
      return null;
    }
    
    return <Component {...props} />;
  };
};

export default AuthContext;
